/* 0.49.43 - Search and filters for a Repeating display.
 *
 * Search: the text typed in the display's Search element. Every word
 * must be found (any part of the text, ignoring capitals) in the row's
 * columns (all of them, or the ones the admin picked). A date column
 * (YYYY-MM-DD) is searched as written on the card too, so "November"
 * (in the page's language) finds the November rows.
 *
 * Filters: one line per control on the page (a dropdown, a date field,
 * a text field...). Each line keeps the rows that pass it, all lines
 * must pass, and a control left empty lets every row through.
 *
 *   equals / contains     text, ignoring capitals (numbers as numbers)
 *   one_of                the control holds several values (a
 *                         multi-select, or a, b, c): equals any of them
 *   date                  the same day
 *   month                 in the month: the control holds 1 to 12, a
 *                         month's name, or a date (that month of that
 *                         year)
 *   year                  in the year: a year, or a date
 *   between               from the control to the second control, both
 *                         days included, either one may be empty
 *   gt / lt               more / less: numbers, or dates in date order
 *
 * A column holding a list passes when one of its items passes. */
import { formatCalendarDate } from '../utils/format';

export const RD_FILTER_TESTS = [
  { value: 'equals', label: 'equals' },
  { value: 'contains', label: 'contains' },
  { value: 'one_of', label: 'is one of (a multi-select)' },
  { value: 'date', label: 'is the date' },
  { value: 'month', label: 'is in the month' },
  { value: 'year', label: 'is in the year' },
  { value: 'between', label: 'is between (two controls)' },
  { value: 'gt', label: 'is more than' },
  { value: 'lt', label: 'is less than' },
];

const text = (v) => (v === null || v === undefined ? '' : String(v)).trim();
const lower = (v) => text(v).toLowerCase();
const asNum = (s) => {
  const t = text(s).replace(/[\s,]/g, '');
  return t !== '' && /^-?\d+(\.\d+)?$/.test(t) ? Number(t) : null;
};

/* YYYY-MM-DD from a stored date, a date and time, or dd.mm.yyyy. */
export function isoDay(v) {
  const s = text(v);
  let m = /^(\d{4})-(\d{2})-(\d{2})/.exec(s);
  if (m) return `${m[1]}-${m[2]}-${m[3]}`;
  m = /^(\d{1,2})[./](\d{1,2})[./](\d{4})$/.exec(s);
  if (m) return `${m[3]}-${m[2].padStart(2, '0')}-${m[1].padStart(2, '0')}`;
  return '';
}

/* A month's number from 1 to 12, from a number or a month's name in
   Armenian, Russian or English (any form starting with the name). */
const MONTH_NAMES = (() => {
  const out = [];
  for (const loc of ['hy', 'ru', 'en']) {
    for (const style of ['long', 'short']) {
      try {
        const f = new Intl.DateTimeFormat(loc, { month: style });
        for (let i = 0; i < 12; i++) out.push([f.format(new Date(2026, i, 15)).toLowerCase().replace(/\.$/, ''), i + 1]);
      } catch (_e) { /* a browser without that language */ }
    }
  }
  // Armenian names written as people write them.
  ['հունվար', 'փետրվար', 'մարտ', 'ապրիլ', 'մայիս', 'հունիս', 'հուլիս', 'օգոստոս',
    'սեպտեմբեր', 'հոկտեմբեր', 'նոյեմբեր', 'դեկտեմբեր'].forEach((n, i) => out.push([n, i + 1]));
  return out.filter(([n]) => n.length >= 3).sort((a, b) => b[0].length - a[0].length);
})();
/* Every name of each month (index 0 = January), for the search. */
const MONTH_WORDS = (() => {
  const out = Array.from({ length: 12 }, () => []);
  for (const [name, m] of MONTH_NAMES) out[m - 1].push(name);
  // Armenian genitive, as dates are written ("11 նոյեմբերի").
  out.forEach((list) => list.filter(n => /[\u0530-\u058F]$/.test(n) && !n.endsWith('ի')).forEach(n => list.push(`${n}ի`)));
  return out;
})();

function monthOf(v) {
  /* 0.49.53 - A Months and days code (m01...m12, or g01...g12 for the
     month as written in a date): what a dropdown of that library stores
     when its Stored value column is left at the library's own key. */
  const code = /^[mg](\d{2})$/i.exec(text(v));
  if (code && Number(code[1]) >= 1 && Number(code[1]) <= 12) return { month: Number(code[1]), year: null };
  const n = asNum(v);
  if (n !== null && n >= 1 && n <= 12) return { month: n, year: null };
  const d = isoDay(v);
  if (d) return { month: Number(d.slice(5, 7)), year: Number(d.slice(0, 4)) };
  const s = lower(v);
  if (!s) return null;
  const hit = MONTH_NAMES.find(([name]) => s.startsWith(name) || name.startsWith(s));
  return hit ? { month: hit[1], year: null } : null;
}

const asList = (v) => (Array.isArray(v) ? v : [v]);
const empty = (v) => (Array.isArray(v) ? v.every(x => text(x) === '') : text(v) === '');

function same(a, b) {
  const na = asNum(a); const nb = asNum(b);
  if (na !== null && nb !== null) return na === nb;
  return lower(a) === lower(b);
}
function order(a, b) {
  const da = isoDay(a); const db = isoDay(b);
  if (da && db) return da < db ? -1 : da > db ? 1 : 0;
  const na = asNum(a); const nb = asNum(b);
  if (na !== null && nb !== null) return na - nb;
  const x = lower(a); const y = lower(b);
  return x < y ? -1 : x > y ? 1 : 0;
}

/* 0.49.51 - A control holding several values (a multi-select: January
   and February) passes when the cell passes for ANY of them. Read as one
   value, [1, 2] became "1,2" and then the number 12 (December). For the
   date tests a comma-separated text ("1, 2") is several values too; for
   equals and contains a text keeps its commas (a lecturer's title). */
const SPLIT_TESTS = new Set(['date', 'month', 'year']);
function wantsOf(want, test) {
  const list = Array.isArray(want) ? want : [want];
  const out = SPLIT_TESTS.has(test)
    ? list.flatMap(w => (typeof w === 'string' && w.includes(',') && !isoDay(w) ? w.split(',') : [w]))
    : list;
  return out.filter(w => text(w) !== '');
}

function passOne(cell, test, want, want2) {
  if (test !== 'one_of' && test !== 'between') {
    const many = wantsOf(want, test);
    if (many.length > 1 || Array.isArray(want)) return many.some(w => passSingle(cell, test, w, want2));
  }
  return passSingle(cell, test, want, want2);
}

function passSingle(cell, test, want, want2) {
  switch (test) {
    case 'equals': return same(cell, want);
    case 'contains': return lower(cell).includes(lower(want));
    case 'one_of': {
      const list = asList(want).flatMap(w => String(w ?? '').split(',')).map(text).filter(Boolean);
      return list.length === 0 || list.some(w => same(cell, w));
    }
    case 'date': { const d = isoDay(cell); return !!d && d === isoDay(want); }
    case 'month': {
      const d = isoDay(cell); const m = monthOf(want);
      if (!d || !m) return false;
      return Number(d.slice(5, 7)) === m.month && (m.year === null || Number(d.slice(0, 4)) === m.year);
    }
    case 'year': {
      const d = isoDay(cell); if (!d) return false;
      const y = isoDay(want) ? Number(isoDay(want).slice(0, 4)) : asNum(want);
      return y !== null && Number(d.slice(0, 4)) === y;
    }
    case 'between': {
      if (!empty(want) && order(cell, want) < 0) return false;
      if (!empty(want2) && order(cell, want2) > 0) return false;
      return true;
    }
    case 'gt': return order(cell, want) > 0;
    case 'lt': return order(cell, want) < 0;
    default: return true;
  }
}

/* The rows that pass every filter line. `valueOf(name)` reads a control. */
export function filterByControls(rows, filters, valueOf, readValue) {
  const lines = (Array.isArray(filters) ? filters : [])
    .filter(f => f && f.control && f.column && f.test)
    .map(f => ({ ...f, want: valueOf(f.control), want2: f.control2 ? valueOf(f.control2) : '' }))
    .filter(f => (f.test === 'between' ? !(empty(f.want) && empty(f.want2)) : !empty(f.want)));
  if (!lines.length) return rows;
  /* 0.49.52 - A date column of a library is shown in the Public site
     format ("11 նոյեմբերի 2026" with dd MMMM yyyy), which is not read
     back as a date, so every date test failed and nothing passed. The
     stored value is kept beside it as <column>_iso: the date tests read
     that one. */
  const DATE_TESTS = new Set(['date', 'month', 'year', 'between', 'gt', 'lt']);
  const cellOf = (row, f) => {
    if (DATE_TESTS.has(f.test)) {
      const iso = readValue(row, `${f.column}_iso`);
      if (iso !== undefined && iso !== null && iso !== '') return iso;
    }
    return readValue(row, f.column);
  };
  return rows.filter(row => lines.every(f => {
    const cell = cellOf(row, f);
    const cells = Array.isArray(cell) ? cell : [cell];
    return cells.some(c => passOne(c, f.test, f.want, f.want2));
  }));
}

/* The words of a search text. */
export function searchWords(q) {
  return lower(q).split(/\s+/).filter(Boolean);
}

/* The rows where every word is found. `columns` empty: all columns. */
export function searchRows(rows, words, columns, readValue) {
  if (!words.length) return rows;
  const cols = Array.isArray(columns) ? columns.filter(Boolean) : [];
  return rows.filter((row) => {
    const parts = [];
    const add = (v) => {
      if (v === null || v === undefined) return;
      if (Array.isArray(v)) { v.forEach(add); return; }
      if (typeof v === 'object') { Object.values(v).forEach(add); return; }
      const s = String(v);
      parts.push(s);
      if (/^\d{4}-\d{2}-\d{2}/.test(s.trim())) {
        const d = s.trim().slice(0, 10);
        parts.push(formatCalendarDate(d));
        // The month's names, so a month typed in any of the site's
        // languages finds it whatever the date format shows.
        parts.push(...(MONTH_WORDS[Number(d.slice(5, 7)) - 1] || []));
      }
    };
    // 0.49.52 - With the column's stored date too (its month names).
    if (cols.length) cols.forEach(c => { add(readValue(row, c)); add(readValue(row, `${c}_iso`)); });
    else if (row && typeof row === 'object') {
      for (const [k, v] of Object.entries(row)) if (!k.startsWith('_')) add(v);
    }
    const hay = parts.join(' ⁣ ').toLowerCase();
    return words.every(w => hay.includes(w));
  });
}
